import { Button, Modal } from '@edgebox/react-components';
import { ISyncCoreContentItem } from '@edgebox/sync-core-data-definitions';
import { ClientContentOptimizationTypeEntity, ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { EmbeddedModal } from '../../../EmbeddedModal';
import { brandPresenceContentItemPath } from '../brandPresencePaths';
import { BP_PORTAL_CLASS_NAME, BP_TOOLBAR_BUTTON_SMALL_CLASSES, BP_WAITING_CONTROL_CLASSES } from './brandPresenceClasses';
import { createCheckOptimization, IStartedOptimization } from './checkOptimization.client';
import { ProgressBar } from './ProgressBar';
import { IOptimizationFollow, useOptimizationFollow } from './useOptimizationFollow';

/** What the action says when the create answered with no optimization or failed without a sentence. */
const OPTIMIZATION_NOT_STARTED_COPY = 'Unable to start optimization.';

/** What the action says when it is chosen while a draft of its page is still being written. */
const PAGE_OPTIMIZATION_WAITING_COPY = 'An optimization of this page is already in progress.';

/** Where an optimization the list follows stands, as a row shows it. */
export type IOptimizationView = Pick<IOptimizationFollow, 'progress' | 'notice' | 'error'> & {
  /** Whether its draft is being written, which the page's rows wait for. */
  writing: boolean;
  /** Whether its draft is there, to be reviewed on the page. */
  draftReady: boolean;
};

/**
 * Follows one optimization of a page for the issue list, the way the page's Health tab follows the one it
 * started, and tells the list where it stands. It renders nothing: the list keeps it for as long as it
 * follows the page, whatever happens to the row that started it, and the row shows what it tells.
 */
export function IssueOptimizationFollower({
  api,
  started,
  onView,
}: {
  api: ISyncCoreApi;
  started: IStartedOptimization;
  onView: (view: IOptimizationView) => void;
}) {
  const [draftReady, setDraftReady] = useState(false);
  const optimization = useOptimizationFollow({ api, onDraftReady: () => setDraftReady(true) });
  const { follow } = optimization;
  useEffect(() => {
    follow(started);
  }, [follow, started]);

  const onViewRef = useRef(onView);
  onViewRef.current = onView;
  // Writing until the follow has told otherwise: from the start, before its first read has landed, and while
  // it follows an optimization the site does not hold. A draft, an end without one and the site's sentence
  // each let the page be optimized again.
  const writing = optimization.optimizationId ? !optimization.notice : !(optimization.error || optimization.notice || draftReady);
  useEffect(() => {
    onViewRef.current({ progress: optimization.progress, notice: optimization.notice, error: optimization.error, writing, draftReady });
  }, [optimization.progress, optimization.notice, optimization.error, writing, draftReady]);

  return null;
}

/**
 * A row's Optimize action: it starts the optimization the page's Health tab starts for the issue's
 * check — the same types, the same page, the same create — once the person confirmed it, and hands it to
 * the list, which follows it. The row then says where it stands the way the Health tab does: in progress,
 * Sync Core's sentence while the site holds the request, or why it ended without a draft. Once the draft
 * is there it leads to the page, where the draft is reviewed.
 *
 * A page is optimized once at a time, as on the Health tab: while a draft of the page is being started or
 * written — from this row or from another row of the same page — the action waits, and says so when it is
 * chosen. Starting one again stays possible while the site holds the request or after it ended.
 */
export function IssueOptimizeAction({
  api,
  page,
  pageKey,
  pageTitle,
  issueLabel,
  types,
  followed,
  pageWaiting,
  onStartingChange,
  onStarted,
}: {
  api: ISyncCoreApi;
  page: ISyncCoreContentItem;
  pageKey: string;
  pageTitle: string;
  /** What the row calls its issue: its type's name. */
  issueLabel: string;
  /** The optimization types that address the issue's type. */
  types: ClientContentOptimizationTypeEntity[];
  /** Where the optimization this row started stands, while the list follows it. */
  followed: IOptimizationView | undefined;
  /** Whether a draft of the page is being started or written for another row. */
  pageWaiting: boolean;
  /**
   * Told when this row starts an optimization of the page and when that start has answered, so the page's
   * other rows wait meanwhile, whatever becomes of this row.
   */
  onStartingChange: (starting: boolean) => void;
  /** Hands the optimization this row started to the list, which follows it. */
  onStarted: (started: IStartedOptimization) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | undefined>(undefined);
  const [waitingNotice, setWaitingNotice] = useState<string | undefined>(undefined);
  // A confirmation starts one optimization: the dialog stays clickable while it leaves.
  const startingRef = useRef(false);
  // While a draft is being started or written the action waits, as the Health tab's does; the button
  // stays in place, so focus handed back to it after the dialog is never lost.
  const waiting = starting || !!followed?.writing || pageWaiting;

  useEffect(() => {
    if (!waiting) {
      setWaitingNotice(undefined);
    }
  }, [waiting]);

  const start = async () => {
    if (!confirming || startingRef.current) {
      return;
    }
    // The page may have been given an optimization from another row while the dialog was open.
    if (waiting) {
      setConfirming(false);
      setWaitingNotice(PAGE_OPTIMIZATION_WAITING_COPY);
      return;
    }
    startingRef.current = true;
    setConfirming(false);
    setStarting(true);
    setStartError(undefined);
    onStartingChange(true);

    try {
      const started = await createCheckOptimization(api, { page, types });
      if (!started) {
        throw new Error(OPTIMIZATION_NOT_STARTED_COPY);
      }
      onStarted(started);
    } catch (error) {
      setStartError((error as Error)?.message || OPTIMIZATION_NOT_STARTED_COPY);
    } finally {
      startingRef.current = false;
      setStarting(false);
      onStartingChange(false);
    }
  };

  // A new start leaves what an earlier one said behind, and a start that failed says that alone, as the
  // Health tab does.
  const view = starting || startError ? undefined : followed;
  const error = startError ?? view?.error;

  return (
    <>
      <button
        type="button"
        className={`${BP_TOOLBAR_BUTTON_SMALL_CLASSES} ${waiting ? BP_WAITING_CONTROL_CLASSES : ''}`}
        aria-label={`Optimize: ${issueLabel}`}
        aria-disabled={waiting || undefined}
        onClick={() => {
          if (waiting) {
            setWaitingNotice(PAGE_OPTIMIZATION_WAITING_COPY);
            return;
          }
          setStartError(undefined);
          setConfirming(true);
        }}
      >
        Optimize
      </button>
      {/* What the action says about the optimization it started, spoken as it changes. It is in the page
          from the start, so the first thing it says is spoken too; empty, it takes no room. */}
      <div role="status" className="max-w-[16rem] whitespace-normal text-left">
        {view?.progress ? (
          <ProgressBar
            className="mt-1.5"
            size="hairline"
            percent={view.progress.progress}
            title={view.progress.title}
            label={<span className="text-xs">{view.progress.title}</span>}
          />
        ) : null}
        {waitingNotice && !view?.progress ? <div className="mt-1.5 text-xs text-slate-600">{waitingNotice}</div> : null}
        {view?.notice ? <div className="mt-1.5 text-xs text-slate-600">{view.notice}</div> : null}
        {error ? <div className="mt-1.5 text-xs text-danger">{error}</div> : null}
        {view?.draftReady ? (
          <Link
            to={brandPresenceContentItemPath({ key: pageKey })}
            className="mt-1.5 inline-block text-xs font-medium text-primary underline decoration-primary/30 underline-offset-2 hover:decoration-primary"
            aria-label={`Review the draft: ${issueLabel}`}
          >
            Review the draft
          </Link>
        ) : null}
      </div>

      {/* The dialog traps focus while it is open and hands it back to the Optimize button once it has gone. */}
      <EmbeddedModal className={BP_PORTAL_CLASS_NAME} show={confirming} onHide={() => setConfirming(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Optimize this page?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2 font-semibold">{pageTitle}</p>
          <p className="mb-0">
            An optimization writes a draft of this page that addresses <strong>{issueLabel}</strong>, for you to review.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void start()}>
            Optimize
          </Button>
        </Modal.Footer>
      </EmbeddedModal>
    </>
  );
}
